import { callApi } from './api';
import Course, { CoursePhase, Lesson, LessonPart } from '@/models/course';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const coursesApiUrl = import.meta.env.VITE_API_URL_V1 + '/courses';

// Admin reads go through `/courses/admin*` — gated by admin role rather than
// subscription plan, so internal admins (typically not flagged Premium) don't
// 403 on list/get. POST/PUT/DELETE stay on `/courses*` (already admin-gated).
const coursesAdminApiUrl = `${coursesApiUrl}/admin`;

export const getCourses = async () => {
    return callApi<Course[]>({
        url: `${coursesAdminApiUrl}`,
        method: 'GET',
    });
};

export const useCourses = () => {
    return useQuery({
        queryKey: ['courses'],
        queryFn: getCourses,
    });
};

export const getCourse = async (courseId: string) => {
    return callApi<Course>({
        url: `${coursesAdminApiUrl}/${courseId}`,
        method: 'GET',
    });
};

export const useCourse = (courseId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['course', courseId],
        queryFn: () => getCourse(courseId),
        enabled: !!courseId && isOpen,
    });
};

/**
 * Lesson + phase IDs ARE sent across updates. Preserving them is what keeps
 * users' per-lesson progress (stored by lesson id in courseProgress docs)
 * stable across edits — if the admin saved a course and we sent new ids,
 * every user's completedLessonIds would point at lesson ids that no longer
 * exist. Only `mediaLink` is excluded — that's a runtime-resolved Mux URL
 * the API rebuilds on every read; sending it back as input is meaningless.
 */
export type CourseMutationPayload = Omit<Course, 'id' | 'created_at' | 'updated_at' | 'imageLink' | 'deleted' | 'deleted_at' | 'lessons' | 'phases'> & {
    lessons: (Omit<Lesson, 'mediaLink' | 'parts'> & { parts?: Omit<LessonPart, 'mediaLink'>[] })[];
    phases?: CoursePhase[];
};

export const createCourse = async (payload: CourseMutationPayload) => {
    return callApi<Course>({
        url: `${coursesApiUrl}`,
        method: 'POST',
        data: payload,
    });
};

export const useCreateCourse = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createCourse,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['courses'] });
        },
    });
};

export const updateCourse = async ({ courseId, ...payload }: CourseMutationPayload & { courseId: string }) => {
    return callApi<Course>({
        url: `${coursesApiUrl}/${courseId}`,
        method: 'PUT',
        data: payload,
    });
};

export const useUpdateCourse = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateCourse,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['courses'] });
            queryClient.invalidateQueries({ queryKey: ['course', variables.courseId] });
        },
    });
};

export const deleteCourse = async ({ courseId }: { courseId: string }) => {
    return callApi({
        url: `${coursesApiUrl}/${courseId}`,
        method: 'DELETE',
    });
};

export const useDeleteCourse = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteCourse,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['courses'] });
        },
    });
};
